// AddonCatalogEditor — the operator's upsell menu (Settings → Add-ons).
//
// Rows are `addon_catalog` (sql/071), org-scoped by RLS so no query carries an org
// filter. The platform ships templates under the platform org; "Add from the menu"
// copies one into this org. What matters per row:
//   • Active      — whether customers see the tile at all
//   • How it's handled — auto (applied immediately, undoable) vs review (waits for you)
//   • Lead time   — how many days before the first visit it can start
//   • Billing     — next invoice / when you accept
import { useCallback, useEffect, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";

const H = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });
const HJ = () => ({ ...H(), "Content-Type": "application/json" });

const KIND_LABEL = { service: "Recurring service", delivery: "Delivery", one_off: "One-time job" };

export function AddonCatalogEditor({ org, showToast }) {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [mine, tpl] = await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/addon_catalog?is_template=is.false&order=sort.asc`, { headers: H() }).then((r) => r.json()),
      // active=true: the platform can retire a starter item ("Offered to operators: No")
      // and it must actually stop being offered. Belt and braces — the client filters
      // again below in case a stale row is cached.
      fetch(`${SUPABASE_URL}/rest/v1/addon_catalog?is_template=is.true&active=is.true&order=sort.asc`, { headers: H() }).then((r) => r.json()),
    ]);
    if (Array.isArray(mine)) setRows(mine);
    if (Array.isArray(tpl)) setTemplates(tpl);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function patch(id, body) {
    setSaving(id);
    const r = await fetch(`${SUPABASE_URL}/rest/v1/addon_catalog?id=eq.${id}`, {
      method: "PATCH", headers: { ...HJ(), Prefer: "return=representation" }, body: JSON.stringify(body),
    });
    setSaving(null);
    if (!r.ok) { showToast && showToast("Couldn't save that.", "error"); return; }
    const [updated] = await r.json();
    setRows((rs) => rs.map((x) => (x.id === id ? updated : x)));
  }

  async function addFromTemplate(t) {
    const body = {
      organization_id: org?.id, key: t.key, name: t.name, tagline: t.tagline, description: t.description,
      kind: t.kind, category: t.category, builtin: t.builtin, price: t.price, cadences: t.cadences,
      approval_mode: t.approval_mode, lead_days: t.lead_days, bill_when: t.bill_when, sort: t.sort, active: false,
    };
    const r = await fetch(`${SUPABASE_URL}/rest/v1/addon_catalog`, {
      method: "POST", headers: { ...HJ(), Prefer: "return=representation" }, body: JSON.stringify(body),
    });
    if (!r.ok) {
      const t2 = await r.text();
      showToast && showToast(/duplicate key/.test(t2) ? "You already have that one." : "Couldn't add that.", "error");
      return;
    }
    setShowAdd(false);
    load();
    showToast && showToast(`${t.name} added — switch it on when you're ready.`, "success");
  }

  const mineKeys = new Set(rows.map((r) => r.key));
  const available = templates.filter((t) => !mineKeys.has(t.key) && t.active !== false);

  if (loading) return <div style={{ color: "var(--text2)", padding: 12 }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Add-ons</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
            What clients can add to their plan from the app. Switch one on and it appears as a tile.
          </div>
        </div>
        {available.length ? (
          <button className="btn btn-sm" onClick={() => setShowAdd(true)}>Add from the menu</button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}>
          Nothing on your menu yet. “Add from the menu” starts you off with the common ones.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((r) => (
            <div key={r.id} className="card" style={{ padding: 14, opacity: r.active ? 1 : 0.72 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{r.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                    {KIND_LABEL[r.kind] || r.kind}
                    {r.builtin === "deo" ? " · uses your deodorizer rate" : r.price != null ? ` · $${Number(r.price).toFixed(2)}${r.kind === "one_off" ? "" : "/mo"}` : ""}
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
                  <input type="checkbox" checked={!!r.active} disabled={saving === r.id}
                         onChange={(e) => patch(r.id, { active: e.target.checked })} />
                  <span style={{ fontSize: 13.5 }}>{r.active ? "On" : "Off"}</span>
                </label>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10, marginTop: 12 }}>
                {r.builtin !== "deo" ? (
                  <label>
                    <span style={{ fontSize: 12, color: "var(--text2)" }}>Price {r.kind === "one_off" ? "(per job)" : "(a month)"}</span>
                    <input className="input" type="number" step="0.01" min="0" defaultValue={r.price ?? ""}
                           onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== r.price) patch(r.id, { price: v }); }}
                           style={{ width: "100%", marginTop: 3 }} />
                  </label>
                ) : null}
                <label>
                  <span style={{ fontSize: 12, color: "var(--text2)" }}>How it's handled</span>
                  <select className="input" value={r.approval_mode} onChange={(e) => patch(r.id, { approval_mode: e.target.value })}
                          style={{ width: "100%", marginTop: 3 }}>
                    <option value="auto">Apply right away (you can undo)</option>
                    <option value="review">Wait for me to accept</option>
                  </select>
                </label>
                <label>
                  <span style={{ fontSize: 12, color: "var(--text2)" }}>Lead time (days)</span>
                  <input className="input" type="number" min="0" max="60" defaultValue={r.lead_days}
                         onBlur={(e) => { const v = Number(e.target.value); if (v !== r.lead_days) patch(r.id, { lead_days: v }); }}
                         style={{ width: "100%", marginTop: 3 }} />
                </label>
                {r.kind === "one_off" ? (
                  <label>
                    <span style={{ fontSize: 12, color: "var(--text2)" }}>Billing</span>
                    <select className="input" value={r.bill_when} onChange={(e) => patch(r.id, { bill_when: e.target.value })}
                            style={{ width: "100%", marginTop: 3 }}>
                      <option value="on_accept">Invoice when I accept</option>
                      <option value="next_invoice">Add to the next invoice</option>
                      <option value="on_complete">Invoice when it's done</option>
                    </select>
                  </label>
                ) : null}
              </div>

              <label style={{ display: "block", marginTop: 10 }}>
                <span style={{ fontSize: 12, color: "var(--text2)" }}>Tile line (what clients read)</span>
                <input className="input" defaultValue={r.tagline || ""} maxLength={80}
                       onBlur={(e) => { if (e.target.value !== (r.tagline || "")) patch(r.id, { tagline: e.target.value || null }); }}
                       style={{ width: "100%", marginTop: 3 }} />
              </label>
            </div>
          ))}
        </div>
      )}

      {showAdd ? (
        <>
          <div className="modal-overlay" onClick={() => setShowAdd(false)} />
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div style={{ fontWeight: 600 }}>Add from the menu</div>
              <button className="close-btn" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: "grid", gap: 8 }}>
              {available.map((t) => (
                <button key={t.id} className="card" onClick={() => addFromTemplate(t)}
                        style={{ padding: 12, textAlign: "left", cursor: "pointer", border: "1px solid var(--border)" }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                    {t.tagline} · {KIND_LABEL[t.kind]}{t.price != null ? ` · $${Number(t.price).toFixed(2)}` : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
